import {useMemo, useState} from "react";
import {css, cx} from "@panda/css";
import {isAfter, parseISO} from "date-fns";

import {Button} from "@pg-design/button-module";
import {CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, FloorIcon} from "@pg-design/icons-module";
import {Placeholder} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, dateTimeFormat, formatDate, pluralize, priceFormat} from "@pg-mono/string-utils";

import {ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {replaceDigitsWithZeros} from "../../credit_calculator/utils/replace_digits_with_zeros";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {hitGTMPropertySelectItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {PropertyListApplicationButtonVariantBCD} from "../../property_list/components/property_list_application_button/PropertyListApplicationButtonVariantBCD";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {AdditionalAreaTypeNames} from "../types/RoomType";

interface Props {
    property: IPropertyConfiguratorProperty;
    gtmMeta: {
        listName: string;
        listId: string;
    };
}

export const HorizontalPropertyBox = (props: Props) => {
    const {property, gtmMeta} = props;

    const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const {shouldHidePrices} = useShouldHidePrices();

    const hasPrice = typeof property.price === "number" && property.price > 0;
    const shouldRenderPrice = hasPrice && (shouldShowPrice || shouldHidePrices);
    const priceText = hasPrice ? priceFormat(property.price!) : "";
    const displayPrice = !shouldShowPrice && priceText ? replaceDigitsWithZeros(priceText) : priceText;

    const mappedAdditionalAreas = useMemo(() => {
        if (property.additional_areas.length === 0) {
            return "";
        }

        if (property.additional_areas.length > 0 && property.additional_areas?.[0]?.area === null) {
            return ` + ${AdditionalAreaTypeNames[property.additional_areas[0].type - 1]}`;
        }

        const additionalAreas = property.additional_areas
            .filter((area) => !!area.area)
            .map((area) => `${AdditionalAreaTypeNames[area.type - 1]} ${area.area}m²`);
        return ` + ${additionalAreas.join(" + ")}`;
    }, []);

    const externalPlanUrl = property?.external_plan_url;
    const mainImage = externalPlanUrl || property.plan_image?.p_img_375x250;
    const [imageIndex, setImageIndex] = useState(0);

    const gallery = useMemo(() => {
        if (!property.gallery.length && !mainImage) {
            return [];
        }
        if (!property.gallery.length) {
            return [mainImage];
        }
        return [mainImage, ...property.gallery.map((image) => image.image.g_img_500)];
    }, []);

    const handleIndexChange = (value: number) => {
        if (imageIndex + value > gallery.length - 1) {
            return setImageIndex(0);
        }
        if (imageIndex + value === -1) {
            return setImageIndex(gallery.length - 1);
        }
        setImageIndex((prev) => prev + value);
    };

    const isBuild = isAfter(new Date(), parseISO(property.offer.construction_date_range?.upper as string));
    const date = formatDate(property.offer.construction_date_range?.upper, dateTimeFormat.quarter);

    const reportSelection = () => {
        hitGTMPropertySelectItem({
            offer: {
                ...property.offer,
                construction_date_range: {
                    upper: property.construction_end_date
                }
            },
            property: property,
            listName: gtmMeta.listName,
            listId: gtmMeta.listId
        });
    };

    return (
        <a href={propertyLink} target="_blank" className={wrapper} onClick={reportSelection}>
            <FavoriteButton offer={property.offer} property={property} display={{type: "icon"}} className={favoriteButton} />
            <div className={mainImage ? imageWrapper : imagePlaceholderWrapper}>
                {gallery.length > 1 && (
                    <ChevronLeftIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(-1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
                {!!gallery.length ? <img src={gallery[imageIndex]} alt="plan mieszkania" /> : <Placeholder />}
                {gallery.length > 1 && (
                    <ChevronRightIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
            </div>
            <div className={propertyTextContainer}>
                <Text variant="headline_5" as="p" mb={1}>
                    {property.rooms} {roomsPluralize(property.rooms)}, {areaFormat(property.area)}
                    {mappedAdditionalAreas}
                </Text>
                <Text variant="info_txt_1">{property.offer.name}</Text>
                <Text variant="info_txt_1">{property.offer.address}</Text>
                <div className={specsContainer}>
                    <div className={iconContainer}>
                        <CalendarCheckIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" className={css({ml: 0.5, mr: 2})}>
                            {isBuild ? "Gotowe do odbioru" : date}
                        </Text>
                        <div className={tooltipStyle}>Termin oddania: {isBuild ? "Gotowe do odbioru" : date}</div>
                    </div>
                    <div className={iconContainer}>
                        <FloorIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" className={css({ml: 0.5})}>
                            {property.floor || "Parter"}
                        </Text>
                        <div className={tooltipStyle}>Piętro: {property.floor || "Parter"}</div>
                    </div>
                </div>
                {shouldRenderPrice && (
                    <Text variant="info_txt_1" className={cx(css({pb: 2}), !shouldShowPrice && blurredPriceStyle)} aria-hidden={shouldShowPrice}>
                        <Highlight>
                            <Text variant="headline_1" as="span">
                                {displayPrice}&nbsp;
                            </Text>
                        </Highlight>
                    </Text>
                )}
                <div className={buttonsWrapper}>
                    <PropertyListApplicationButtonVariantBCD
                        property={property}
                        isAutoresponderEnabled={property.offer.configuration.is_autoresponder_enabled}
                        vendorId={property.offer.vendor.id}
                        isInPropertyConfigurator
                        isPlansList
                        applicationSourceSection={ApplicationSourceSection.PROPERTY_CONFIGURATOR}
                        onLeadBtnClick={reportSelection}
                    />
                    <Button
                        variant="none_secondary"
                        size="small"
                        onClick={() => {
                            propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_OFFER_CLICK);
                        }}
                    >
                        Zobacz szczegóły
                    </Button>
                </div>
            </div>
        </a>
    );
};

const wrapper = css({
    display: "flex",
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    gap: 3,
    boxShadow: "var(--shadow-1)",
    overflow: "hidden",
    minHeight: "initial",
    maxHeight: "initial",
    position: "relative",
    flexDirection: "column",
    px: 2,
    pt: 1,
    pb: 2,

    lg: {
        flexDirection: "row",
        gap: 4,
        minHeight: "262px",
        maxHeight: "fit-content",
        pr: 2,
        pl: 1,
        py: 1
    },

    "&:hover,&:focus,&:active": {
        color: "initial"
    }
});

const propertyTextContainer = css({
    display: "flex",
    flexDirection: "column",
    mt: 1.5,
    mb: 1,
    width: "100%"
});

const buttonsWrapper = css({
    display: "flex",
    mt: "auto",
    gap: 2,
    flexDirection: "column",

    "& > button:last-of-type ": {
        maxHeight: "var(--spacing-5)"
    },

    "&> button:first-of-type ": {
        maxWidth: "initial"
    },
    lg: {
        flexDirection: "row",
        "&> button:first-of-type ": {
            maxWidth: "247px"
        }
    }
});

const specsContainer = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
    mt: 2.5
});

const imagePlaceholderWrapper = css({
    position: "relative",
    height: "204px",
    width: "100%",
    maxWidth: "100%",
    maxHeight: "204px",

    "&>div": {
        height: "calc(100% + var(--spacing-2))",
        width: "calc(100% + var(--spacing-4))",
        top: -1,
        left: -2
    },

    lg: {
        maxWidth: "417px",
        maxHeight: "100%",
        height: "100%"
    }
});

const imageWrapper = css({
    height: "246px",
    width: "100%",
    maxWidth: "100%",
    maxHeight: "204px",
    minHeight: "204px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    position: "relative",

    "&> img": {
        maxWidth: "100%",
        maxHeight: "100%"
    },

    "&> div ": {
        position: "absolute",
        top: "50%",
        translate: "0 -50%"
    },

    "&> div:first-of-type": {
        left: 0
    },

    "&> div:last-of-type": {
        right: 0
    },

    lg: {
        width: "100%",
        maxWidth: "417px",
        maxHeight: "100%"
    }
});

const tooltipStyle = css({
    alignItems: "center",
    justifyContent: "center",
    display: "none",
    p: 0.5,
    color: "var(--colors-gray-700)",
    borderRadius: 1,
    position: "absolute",
    left: 0,
    top: 0,
    translate: "0 -100%",
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-1)",
    whiteSpace: "nowrap",
    zIndex: 1
});

const iconContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    gap: 1,

    "&:hover div": {
        display: "flex"
    }
});

const favoriteButton = css({
    position: "absolute",
    top: 1,
    right: 1,
    zIndex: 1
});

const blurredPriceStyle = css({
    filter: "blur(8px)",
    userSelect: "none",
    pointerEvents: "none"
});
import {useMemo, useState} from "react";
import {css, cx} from "@panda/css";
import {isAfter, parseISO} from "date-fns";

import {Button} from "@pg-design/button-module";
import {CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, FloorIcon} from "@pg-design/icons-module";
import {Placeholder} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, dateTimeFormat, formatDate, pluralize, priceFormat} from "@pg-mono/string-utils";

import {ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {replaceDigitsWithZeros} from "../../credit_calculator/utils/replace_digits_with_zeros";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {hitGTMPropertySelectItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {PropertyListApplicationButtonVariantBCD} from "../../property_list/components/property_list_application_button/PropertyListApplicationButtonVariantBCD";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {AdditionalAreaTypeNames} from "../types/RoomType";

interface Props {
    property: IPropertyConfiguratorProperty;
    gtmMeta: {
        listName: string;
        listId: string;
    };
}

export const HorizontalPropertyBox = (props: Props) => {
    const {property, gtmMeta} = props;

    const roomsPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const {shouldHidePrices} = useShouldHidePrices();

    const hasPrice = typeof property.price === "number" && property.price > 0;
    const shouldRenderPrice = hasPrice && (shouldShowPrice || shouldHidePrices);
    const priceText = hasPrice ? priceFormat(property.price!) : "";
    const displayPrice = !shouldShowPrice && priceText ? replaceDigitsWithZeros(priceText) : priceText;

    const mappedAdditionalAreas = useMemo(() => {
        if (property.additional_areas.length === 0) {
            return "";
        }

        if (property.additional_areas.length > 0 && property.additional_areas?.[0]?.area === null) {
            return ` + ${AdditionalAreaTypeNames[property.additional_areas[0].type - 1]}`;
        }

        const additionalAreas = property.additional_areas
            .filter((area) => !!area.area)
            .map((area) => `${AdditionalAreaTypeNames[area.type - 1]} ${area.area}m²`);
        return ` + ${additionalAreas.join(" + ")}`;
    }, []);

    const externalPlanUrl = property?.external_plan_url;
    const mainImage = externalPlanUrl || property.plan_image?.p_img_375x250;
    const [imageIndex, setImageIndex] = useState(0);

    const gallery = useMemo(() => {
        if (!property.gallery.length && !mainImage) {
            return [];
        }
        if (!property.gallery.length) {
            return [mainImage];
        }
        return [mainImage, ...property.gallery.map((image) => image.image.g_img_500)];
    }, []);

    const handleIndexChange = (value: number) => {
        if (imageIndex + value > gallery.length - 1) {
            return setImageIndex(0);
        }
        if (imageIndex + value === -1) {
            return setImageIndex(gallery.length - 1);
        }
        setImageIndex((prev) => prev + value);
    };

    const isBuild = isAfter(new Date(), parseISO(property.offer.construction_date_range?.upper as string));
    const date = formatDate(property.offer.construction_date_range?.upper, dateTimeFormat.quarter);

    const reportSelection = () => {
        hitGTMPropertySelectItem({
            offer: {
                ...property.offer,
                construction_date_range: {
                    upper: property.construction_end_date
                }
            },
            property: property,
            listName: gtmMeta.listName,
            listId: gtmMeta.listId
        });
    };

    return (
        <a href={propertyLink} target="_blank" className={wrapper} onClick={reportSelection}>
            <FavoriteButton offer={property.offer} property={property} display={{type: "icon"}} className={favoriteButton} />
            <div className={mainImage ? imageWrapper : imagePlaceholderWrapper}>
                {gallery.length > 1 && (
                    <ChevronLeftIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(-1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
                {!!gallery.length ? <img src={gallery[imageIndex]} alt="plan mieszkania" /> : <Placeholder />}
                {gallery.length > 1 && (
                    <ChevronRightIcon
                        onClick={(e) => {
                            e.preventDefault();
                            handleIndexChange(1);
                        }}
                        wrapperSize="2.4"
                        size="2.4"
                        wrapperColor="transparent"
                    />
                )}
            </div>
            <div className={propertyTextContainer}>
                <Text variant="headline_5" as="p" mb={1}>
                    {property.rooms} {roomsPluralize(property.rooms)}, {areaFormat(property.area)}
                    {mappedAdditionalAreas}
                </Text>
                <Text variant="info_txt_1">{property.offer.name}</Text>
                <Text variant="info_txt_1">{property.offer.address}</Text>
                <div className={specsContainer}>
                    <div className={iconContainer}>
                        <CalendarCheckIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" className={css({ml: 0.5, mr: 2})}>
                            {isBuild ? "Gotowe do odbioru" : date}
                        </Text>
                        <div className={tooltipStyle}>Termin oddania: {isBuild ? "Gotowe do odbioru" : date}</div>
                    </div>
                    <div className={iconContainer}>
                        <FloorIcon wrapperSize="2.4" size="2.4" wrapperColor="transparent" />
                        <Text variant="info_txt_1" className={css({ml: 0.5})}>
                            {property.floor || "Parter"}
                        </Text>
                        <div className={tooltipStyle}>Piętro: {property.floor || "Parter"}</div>
                    </div>
                </div>
                {shouldRenderPrice && (
                    <Text variant="info_txt_1" className={cx(css({pb: 2}), !shouldShowPrice && blurredPriceStyle)} aria-hidden={shouldShowPrice}>
                        <Highlight>
                            <Text variant="headline_1" as="span">
                                {displayPrice}&nbsp;
                            </Text>
                        </Highlight>
                    </Text>
                )}
                <div className={buttonsWrapper}>
                    <PropertyListApplicationButtonVariantBCD
                        property={property}
                        isAutoresponderEnabled={property.offer.configuration.is_autoresponder_enabled}
                        vendorId={property.offer.vendor.id}
                        isInPropertyConfigurator
                        isPlansList
                        applicationSourceSection={ApplicationSourceSection.PROPERTY_CONFIGURATOR}
                        onLeadBtnClick={reportSelection}
                    />
                    <Button
                        variant="none_secondary"
                        size="small"
                        onClick={() => {
                            propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_OFFER_CLICK);
                        }}
                    >
                        Zobacz szczegóły
                    </Button>
                </div>
            </div>
        </a>
    );
};

const wrapper = css({
    display: "flex",
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    gap: 3,
    boxShadow: "var(--shadow-1)",
    overflow: "hidden",
    minHeight: "initial",
    maxHeight: "initial",
    position: "relative",
    flexDirection: "column",
    px: 2,
    pt: 1,
    pb: 2,

    lg: {
        flexDirection: "row",
        gap: 4,
        minHeight: "262px",
        maxHeight: "fit-content",
        pr: 2,
        pl: 1,
        py: 1
    },

    "&:hover,&:focus,&:active": {
        color: "initial"
    }
});

const propertyTextContainer = css({
    display: "flex",
    flexDirection: "column",
    mt: 1.5,
    mb: 1,
    width: "100%"
});

const buttonsWrapper = css({
    display: "flex",
    mt: "auto",
    gap: 2,
    flexDirection: "column",

    "& > button:last-of-type ": {
        maxHeight: "var(--spacing-5)"
    },

    "&> button:first-of-type ": {
        maxWidth: "initial"
    },
    lg: {
        flexDirection: "row",
        "&> button:first-of-type ": {
            maxWidth: "247px"
        }
    }
});

const specsContainer = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
    mt: 2.5
});

const imagePlaceholderWrapper = css({
    position: "relative",
    height: "204px",
    width: "100%",
    maxWidth: "100%",
    maxHeight: "204px",

    "&>div": {
        height: "calc(100% + var(--spacing-2))",
        width: "calc(100% + var(--spacing-4))",
        top: -1,
        left: -2
    },

    lg: {
        maxWidth: "417px",
        maxHeight: "100%",
        height: "100%"
    }
});

const imageWrapper = css({
    height: "246px",
    width: "100%",
    maxWidth: "100%",
    maxHeight: "204px",
    minHeight: "204px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    position: "relative",

    "&> img": {
        maxWidth: "100%",
        maxHeight: "100%"
    },

    "&> div ": {
        position: "absolute",
        top: "50%",
        translate: "0 -50%"
    },

    "&> div:first-of-type": {
        left: 0
    },

    "&> div:last-of-type": {
        right: 0
    },

    lg: {
        width: "100%",
        maxWidth: "417px",
        maxHeight: "100%"
    }
});

const tooltipStyle = css({
    alignItems: "center",
    justifyContent: "center",
    display: "none",
    p: 0.5,
    color: "var(--colors-gray-700)",
    borderRadius: 1,
    position: "absolute",
    left: 0,
    top: 0,
    translate: "0 -100%",
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-1)",
    whiteSpace: "nowrap",
    zIndex: 1
});

const iconContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    gap: 1,

    "&:hover div": {
        display: "flex"
    }
});

const favoriteButton = css({
    position: "absolute",
    top: 1,
    right: 1,
    zIndex: 1
});

const blurredPriceStyle = css({
    filter: "blur(8px)",
    userSelect: "none",
    pointerEvents: "none"
});
